import { Heading, Stack, Input, Button } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';
import { User } from '@/lib/types';

interface Props {
  user: User;
}

const formSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Confirm password is required'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof formSchema>;

export default function UpdatePassword({ user }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await api.put(`users/password`, {
        id: user.id,
        password: data.password,
        c_password: data.confirmPassword,
      });
      toaster.create({
        type: 'success',
        title: 'Password updated successfully!',
      });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update password',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={6} align={'flex-start'}>
        <Heading size='xl'>Update password</Heading>
        <Field label='Password' invalid={!!errors.password} errorText={errors.password?.message}>
          <Input {...register('password')} type='password' />
        </Field>
        <Field
          label='Confirm password'
          invalid={!!errors.confirmPassword}
          errorText={errors.confirmPassword?.message}
        >
          <Input {...register('confirmPassword')} type='password' />
        </Field>
        <Button
          colorPalette='brand'
          type={'submit'}
          width={'full'}
          loading={isSubmitting}
          loadingText='Loading...'
        >
          Save
        </Button>
      </Stack>
    </form>
  );
}
